import { showAILoginRequiredAtom } from '@affine/core/components/affine/auth/ai-login-required';
import type { AIToolsConfig } from '@affine/core/modules/ai-button';
import {
  addContextBlobMutation,
  addContextCategoryMutation,
  addContextDocMutation,
  addContextFileMutation,
  cleanupCopilotSessionMutation,
  createCopilotContextMutation,
  createCopilotMessageMutation,
  createCopilotSessionMutation,
  forkCopilotSessionMutation,
  getCopilotHistoriesQuery,
  getCopilotHistoryIdsQuery,
  getCopilotRecentSessionsQuery,
  getCopilotSessionQuery,
  getCopilotSessionsQuery,
  getWorkspaceEmbeddingStatusQuery,
  type GraphQLQuery,
  listContextObjectQuery,
  listContextQuery,
  matchContextQuery,
  type PaginationInput,
  type QueryOptions,
  type QueryResponse,
  removeContextBlobMutation,
  removeContextCategoryMutation,
  removeContextDocMutation,
  removeContextFileMutation,
  type RequestOptions,
  updateCopilotSessionMutation,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import type {
  WorkspaceViewRevision,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { getCurrentStore } from '@toeverything/infra';

import type { ReasoningModeId } from '../../../modules/ai-button/services/models';
import {
  type BudgetExceededCode,
  BudgetExceededError,
  GeneralNetworkError,
  InsufficientCreditsError,
  PaymentRequiredError,
  QuotaExhaustedError,
  UnauthorizedError,
} from './error';

export enum Endpoint {
  Stream = 'stream',
  StreamObject = 'stream-object',
  Images = 'images',
}

type OptionsField<T extends GraphQLQuery> =
  RequestOptions<T>['variables'] extends { options: infer U } ? U : never;

const QUOTA_ERROR_CODES = new Set([
  'insufficient_quota',
  'billing_hard_limit',
  'rate_limit_error',
  'quota_exceeded',
  'COPILOT_LIMIT_REACHED',
  'COPILOT_NOT_AVAILABLE',
]);

function codeToError(error: any) {
  // GraphQL errors from AuthGuard carry extensions.code = 'UNAUTHENTICATED'
  const gqlCode = error.extensions?.code;
  if (gqlCode === 'UNAUTHENTICATED') {
    return new UnauthorizedError();
  }

  // sl-8m4o.1: StreamLain managed credits exhausted — not an error, expected behavior
  if (error.code === 'INSUFFICIENT_CREDITS') {
    return new InsufficientCreditsError(error.message);
  }

  // sl-ufq6.4.1: Budget limit exceeded (user, group, automation)
  const BUDGET_CODES = new Set([
    'MANAGED_AI_ACCESS_DENIED',
    'USER_BUDGET_EXCEEDED',
    'GROUP_BUDGET_EXCEEDED',
    'AUTOMATION_BUDGET_EXCEEDED',
  ]);
  if (error.code && BUDGET_CODES.has(error.code)) {
    return new BudgetExceededError(
      error.code as BudgetExceededCode,
      error.message,
      error.resetAt
    );
  }

  // The image model cannot take the attached picture as a reference.
  if (error.code === 'image_reference_unsupported') {
    return new GeneralNetworkError(
      I18n.t('com.streamlain.ai.image.error.reference-unsupported')
    );
  }

  // Every AI stream place of the workspace is taken by running answers.
  if (error.code === 'WORKSPACE_AI_STREAMS_BUSY') {
    return new GeneralNetworkError(
      I18n.t('com.streamlain.ai.error.workspace-streams-busy')
    );
  }

  // Detect quota exhaustion errors by error code (from SSE payload)
  if (error.code && QUOTA_ERROR_CODES.has(error.code)) {
    return new QuotaExhaustedError(
      error.keySource || 'server',
      error.message || 'API quota exhausted'
    );
  }

  switch (error.status) {
    case 401:
      return new UnauthorizedError();
    case 402:
      return new PaymentRequiredError();
    default:
      return new GeneralNetworkError(
        [error.code, error.message].filter(Boolean).join(': ') +
          (error.name ? `\nIdentify: ${error.name}` : '')
      );
  }
}

export function resolveError(err: any) {
  return codeToError(err);
}

export function handleError(src: any) {
  const err = resolveError(src);
  if (err instanceof UnauthorizedError) {
    getCurrentStore().set(showAILoginRequiredAtom, true);
  }
  return err;
}

/** What a chat stream request sends; a queued message sends the same. */
export interface ChatStreamOptions {
  sessionId: string;
  threadId: string; // Required for thread isolation
  messageId?: string;
  reasoning?: boolean;
  reasoningEffort?: ReasoningModeId;
  webSearch?: boolean;
  modelId?: string;
  toolsConfig?: AIToolsConfig;
  enableTools?: boolean;
  // E010-T015: Viewport position for Edgeless canvas
  viewportX?: number;
  viewportY?: number;
  // sl-535: User language for AI responses (ISO 639-1 code)
  language?: string;
  // sl-ddz8.5.3: Bot ID for Expert Agent chat
  mateId?: string;
  // Closed-loop canvas mutations: per-tab client identity + active doc +
  // workspace so the backend can bind client-side actions to this tab.
  clientInstanceId?: string;
  activeDocId?: string;
  workspaceId?: string;
  workspaceViewScope?: WorkspaceViewScope;
  workspaceViewRevision?: WorkspaceViewRevision;
}

/** A message waiting in a chat's queue, in the order the queue runs it. */
export interface QueuedChatMessage {
  messageId: string;
  /** Goes into the running answer at once. */
  steer: boolean;
  content: string;
}

export class CopilotClient {
  constructor(
    readonly gql: <Query extends GraphQLQuery>(
      options: QueryOptions<Query>
    ) => Promise<QueryResponse<Query>>,
    readonly fetcher: (input: string, init?: RequestInit) => Promise<Response>,
    readonly eventSource: (
      url: string,
      eventSourceInitDict?: EventSourceInit
    ) => EventSource
  ) {}

  async createSession(
    options: OptionsField<typeof createCopilotSessionMutation>
  ) {
    try {
      const res = await this.gql({
        query: createCopilotSessionMutation,
        variables: {
          options,
        },
      });
      return res.createCopilotSession;
    } catch (err) {
      throw resolveError(err);
    }
  }

  async updateSession(
    options: OptionsField<typeof updateCopilotSessionMutation>
  ) {
    try {
      const res = await this.gql({
        query: updateCopilotSessionMutation,
        variables: {
          options,
        },
      });
      return res.updateCopilotSession;
    } catch (err) {
      throw resolveError(err);
    }
  }

  async forkSession(options: OptionsField<typeof forkCopilotSessionMutation>) {
    try {
      const res = await this.gql({
        query: forkCopilotSessionMutation,
        variables: {
          options,
        },
      });
      return res.forkCopilotSession;
    } catch (err) {
      throw resolveError(err);
    }
  }

  async createMessage(
    options: OptionsField<typeof createCopilotMessageMutation>,
    signal?: AbortSignal,
    assertContextCurrent?: () => void
  ) {
    let contextCheckFailed = false;
    const checkContext = assertContextCurrent
      ? () => {
          try {
            assertContextCurrent();
          } catch (error) {
            contextCheckFailed = true;
            throw error;
          }
        }
      : undefined;
    try {
      const res = await this.gql({
        query: createCopilotMessageMutation,
        signal,
        assertContextCurrent: checkContext,
        variables: {
          options,
        },
      });
      return res.createCopilotMessage;
    } catch (err) {
      if (signal?.aborted) throw signal.reason;
      // Preserve the first failed guard, including its original throw site.
      // Rechecking here used to replace request-preparation failures with a
      // second send-boundary error and hide the reason the send stopped.
      if (contextCheckFailed) throw err;
      assertContextCurrent?.();
      throw resolveError(err);
    }
  }

  async getSession(workspaceId: string, sessionId: string) {
    try {
      const res = await this.gql({
        query: getCopilotSessionQuery,
        variables: { sessionId, workspaceId },
      });
      return res.currentUser?.copilot?.chats?.edges?.[0]?.node;
    } catch (err) {
      throw resolveError(err);
    }
  }

  async getSessions(
    workspaceId: string,
    pagination: PaginationInput,
    docId?: string,
    options?: RequestOptions<
      typeof getCopilotSessionsQuery
    >['variables']['options'],
    signal?: AbortSignal
  ) {
    try {
      const res = await this.gql({
        query: getCopilotSessionsQuery,
        variables: {
          workspaceId,
          pagination,
          docId,
          options,
        },
        signal,
      });
      return res.currentUser?.copilot?.chats.edges.map(e => e.node);
    } catch (err) {
      throw resolveError(err);
    }
  }

  async getRecentSessions(
    workspaceId: string,
    limit?: number,
    offset?: number
  ) {
    try {
      const res = await this.gql({
        query: getCopilotRecentSessionsQuery,
        variables: {
          workspaceId,
          limit,
          offset,
        },
      });
      return res.currentUser?.copilot?.chats.edges.map(e => e.node);
    } catch (err) {
      throw resolveError(err);
    }
  }

  async getHistories(
    workspaceId: string,
    pagination: PaginationInput,
    docId?: string,
    options?: RequestOptions<
      typeof getCopilotHistoriesQuery
    >['variables']['options']
  ) {
    try {
      const res = await this.gql({
        query: getCopilotHistoriesQuery,
        variables: {
          workspaceId,
          pagination,
          docId,
          options,
        },
      });

      return res.currentUser?.copilot?.chats.edges.map(e => e.node);
    } catch (err) {
      throw resolveError(err);
    }
  }

  async getHistoryIds(
    workspaceId: string,
    pagination: PaginationInput,
    docId?: string,
    options?: RequestOptions<
      typeof getCopilotHistoryIdsQuery
    >['variables']['options']
  ) {
    try {
      const res = await this.gql({
        query: getCopilotHistoryIdsQuery,
        variables: {
          workspaceId,
          pagination,
          docId,
          options,
        },
      });

      return res.currentUser?.copilot?.chats.edges.map(e => e.node);
    } catch (err) {
      throw resolveError(err);
    }
  }

  async cleanupSessions(input: {
    workspaceId: string;
    docId: string | undefined;
    sessionIds: string[];
  }) {
    try {
      const res = await this.gql({
        query: cleanupCopilotSessionMutation,
        variables: {
          input,
        },
      });
      return res.cleanupCopilotSession;
    } catch (err) {
      throw resolveError(err);
    }
  }

  async createContext(workspaceId: string, sessionId: string) {
    const res = await this.gql({
      query: createCopilotContextMutation,
      variables: {
        workspaceId,
        sessionId,
      },
    });
    return res.createCopilotContext;
  }

  async getContextId(workspaceId: string, sessionId: string) {
    const res = await this.gql({
      query: listContextQuery,
      variables: {
        workspaceId,
        sessionId,
      },
    });
    return res.currentUser?.copilot?.contexts?.[0]?.id || undefined;
  }

  async addContextDoc(options: OptionsField<typeof addContextDocMutation>) {
    const res = await this.gql({
      query: addContextDocMutation,
      variables: {
        options,
      },
    });
    return res.addContextDoc;
  }

  async removeContextDoc(
    options: OptionsField<typeof removeContextDocMutation>
  ) {
    const res = await this.gql({
      query: removeContextDocMutation,
      variables: {
        options,
      },
    });
    return res.removeContextDoc;
  }

  async addContextFile(
    content: File,
    options: OptionsField<typeof addContextFileMutation>
  ) {
    const res = await this.gql({
      query: addContextFileMutation,
      variables: {
        content,
        options,
      },
      timeout: 60000,
    });
    return res.addContextFile;
  }

  async removeContextFile(
    options: OptionsField<typeof removeContextFileMutation>
  ) {
    const res = await this.gql({
      query: removeContextFileMutation,
      variables: {
        options,
      },
    });
    return res.removeContextFile;
  }

  async addContextCategory(
    options: OptionsField<typeof addContextCategoryMutation>
  ) {
    const res = await this.gql({
      query: addContextCategoryMutation,
      variables: {
        options,
      },
    });
    return res.addContextCategory;
  }

  async removeContextCategory(
    options: OptionsField<typeof removeContextCategoryMutation>
  ) {
    const res = await this.gql({
      query: removeContextCategoryMutation,
      variables: {
        options,
      },
    });
    return res.removeContextCategory;
  }

  async getContextDocsAndFiles(
    workspaceId: string,
    sessionId: string,
    contextId: string
  ) {
    const res = await this.gql({
      query: listContextObjectQuery,
      variables: {
        workspaceId,
        sessionId,
        contextId,
      },
    });
    return res.currentUser?.copilot?.contexts?.[0];
  }

  async matchContext(
    content: string,
    contextId?: string,
    workspaceId?: string,
    limit?: number,
    scopedThreshold?: number,
    threshold?: number
  ) {
    const res = await this.gql({
      query: matchContextQuery,
      variables: {
        content,
        contextId,
        workspaceId,
        limit,
        scopedThreshold,
        threshold,
      },
    });
    const { matchFiles: files, matchWorkspaceDocs: docs } =
      res.currentUser?.copilot?.contexts?.[0] || {};
    return { files, docs };
  }

  // sl-535: Added language parameter for tiered i18n support
  async chatText({
    sessionId,
    threadId,
    workspaceId,
    messageId,
    reasoning,
    reasoningEffort,
    webSearch,
    modelId,
    toolsConfig,
    enableTools,
    language,
    signal,
  }: {
    sessionId: string;
    threadId: string; // Required for thread isolation
    workspaceId: string; // Copilot sessions live in the workspace database
    messageId?: string;
    reasoning?: boolean;
    reasoningEffort?: ReasoningModeId;
    webSearch?: boolean;
    modelId?: string;
    toolsConfig?: AIToolsConfig;
    enableTools?: boolean;
    language?: string;
    signal?: AbortSignal;
  }) {
    let url = `/api/copilot/chat/${sessionId}`;
    const queryString = this.paramsToQueryString({
      threadId,
      workspaceId,
      messageId,
      reasoning,
      reasoningEffort,
      webSearch,
      modelId,
      toolsConfig,
      enableTools: enableTools?.toString(),
      language,
    });
    if (queryString) {
      url += `?${queryString}`;
    }
    const response = await this.fetcher(url.toString(), { signal });
    return response.text();
  }

  // Text or image to text
  // sl-canvas2-8.31 Phase 6: disableTools removed - maxToolIterations handles loop prevention
  // sl-535: Added language parameter for tiered i18n support
  chatTextStream(options: ChatStreamOptions, endpoint = Endpoint.Stream) {
    let url = `/api/copilot/chat/${options.sessionId}/${endpoint}`;
    const queryString = this.chatStreamQuery(options);
    if (queryString) {
      url += `?${queryString}`;
    }
    return this.eventSource(url);
  }

  private chatStreamQuery({
    threadId,
    messageId,
    reasoning,
    reasoningEffort,
    webSearch,
    modelId,
    toolsConfig,
    enableTools,
    viewportX,
    viewportY,
    language,
    mateId,
    clientInstanceId,
    activeDocId,
    workspaceId,
    workspaceViewScope,
    workspaceViewRevision,
  }: ChatStreamOptions) {
    return this.paramsToQueryString({
      threadId,
      messageId,
      reasoning,
      reasoningEffort,
      webSearch,
      modelId,
      toolsConfig,
      enableTools: enableTools?.toString(),
      // E010-T015: Pass viewport to backend
      viewportX: viewportX?.toString(),
      viewportY: viewportY?.toString(),
      // sl-535: Pass language to backend
      language,
      // sl-ddz8.5.3: Pass bot ID to backend
      mateId,
      clientInstanceId,
      activeDocId,
      workspaceId,
      activeViewId: workspaceViewScope?.viewId,
      contextEpoch: workspaceViewRevision?.epoch,
      contextSequence: workspaceViewRevision?.sequence.toString(),
    });
  }

  /**
   * Queues a stored user message while the chat answers. It runs when the
   * answer ends, at once when none runs; the server keeps the queue.
   */
  async queueChatMessage(
    options: ChatStreamOptions & { workspaceId: string }
  ): Promise<{ started: boolean }> {
    const response = await this.fetcher(
      this.chatQueuePath(options.sessionId, options.workspaceId),
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          Object.fromEntries(new URLSearchParams(this.chatStreamQuery(options)))
        ),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to queue chat message (${response.status})`);
    }
    return (await response.json()) as { started: boolean };
  }

  /** The chat's queued messages in the order they run. */
  async chatQueue(
    sessionId: string,
    workspaceId: string
  ): Promise<QueuedChatMessage[]> {
    const response = await this.fetcher(
      this.chatQueuePath(sessionId, workspaceId)
    );
    if (!response.ok) {
      throw new Error(`Failed to read chat queue (${response.status})`);
    }
    return ((await response.json()) as { entries: QueuedChatMessage[] })
      .entries;
  }

  /** A removed message never runs and leaves the chat. */
  async removeQueuedChatMessage(
    sessionId: string,
    workspaceId: string,
    messageId: string
  ): Promise<boolean> {
    const response = await this.fetcher(
      this.chatQueuePath(sessionId, workspaceId, messageId),
      { method: 'DELETE' }
    );
    if (!response.ok) {
      throw new Error(`Failed to remove queued message (${response.status})`);
    }
    return ((await response.json()) as { removed: boolean }).removed;
  }

  /** Gives a queued message to the running answer now. */
  async steerQueuedChatMessage(
    sessionId: string,
    workspaceId: string,
    messageId: string
  ): Promise<boolean> {
    const response = await this.fetcher(
      this.chatQueuePath(sessionId, workspaceId, messageId, 'steer'),
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new Error(`Failed to steer queued message (${response.status})`);
    }
    return ((await response.json()) as { steering: boolean }).steering;
  }

  private chatQueuePath(
    sessionId: string,
    workspaceId: string,
    ...path: string[]
  ) {
    return `/api/copilot/chat/${[sessionId, 'queue', ...path]
      .map(encodeURIComponent)
      .join('/')}?${new URLSearchParams({ workspaceId })}`;
  }

  /**
   * Follows the running answer of a chat session from its first event. The
   * EventSource resumes after the last event it saw when it reconnects.
   */
  attachChatStream(sessionId: string, workspaceId: string) {
    return this.eventSource(
      `/api/copilot/chat/${encodeURIComponent(sessionId)}/stream/attach?${new URLSearchParams({ workspaceId })}`
    );
  }

  /**
   * Stop of a chat answer. Closing the EventSource leaves the answer running
   * on the server; only this request ends it.
   */
  async abortChatStream(sessionId: string, workspaceId: string): Promise<void> {
    const response = await this.fetcher(
      `/api/copilot/chat/${encodeURIComponent(sessionId)}/abort?${new URLSearchParams({ workspaceId })}`,
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new Error(`Failed to abort chat stream (${response.status})`);
    }
  }

  // Text or image to images
  imagesStream(
    sessionId: string,
    threadId: string, // Required for thread isolation
    workspaceId: string, // Copilot sessions live in the workspace database
    messageId?: string,
    seed?: string,
    mateId?: string,
    endpoint = Endpoint.Images
  ) {
    let url = `/api/copilot/chat/${sessionId}/${endpoint}`;
    const queryString = this.paramsToQueryString({
      threadId,
      workspaceId,
      messageId,
      seed,
      mateId,
    });
    if (queryString) {
      url += `?${queryString}`;
    }
    return this.eventSource(url);
  }

  paramsToQueryString(
    params: Record<string, string | boolean | undefined | Record<string, any>>
  ) {
    const queryString = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        if (value) {
          queryString.append(key, 'true');
        }
      } else if (typeof value === 'string') {
        queryString.append(key, value);
      } else if (typeof value === 'object' && value !== null) {
        queryString.append(key, JSON.stringify(value));
      }
    });
    return queryString.toString();
  }

  getEmbeddingStatus(workspaceId: string) {
    return this.gql({
      query: getWorkspaceEmbeddingStatusQuery,
      variables: { workspaceId },
    }).then(res => res.queryWorkspaceEmbeddingStatus);
  }

  addContextBlob(options: OptionsField<typeof addContextBlobMutation>) {
    return this.gql({
      query: addContextBlobMutation,
      variables: {
        options,
      },
    }).then(res => res.addContextBlob);
  }

  removeContextBlob(options: OptionsField<typeof removeContextBlobMutation>) {
    return this.gql({
      query: removeContextBlobMutation,
      variables: {
        options,
      },
    }).then(res => res.removeContextBlob);
  }
}
